import { useState, useEffect, useRef, useCallback } from "react";

export type ConnectionQuality = "excellent" | "good" | "fair" | "poor" | "disconnected";

interface ConnectionStats {
  quality: ConnectionQuality;
  bitrate: number;
  packetLoss: number;
  roundTripTime: number;
}

export const useConnectionQuality = (peerConnection: RTCPeerConnection | null) => {
  const [stats, setStats] = useState<ConnectionStats>({
    quality: "disconnected",
    bitrate: 0,
    packetLoss: 0,
    roundTripTime: 0,
  });

  const previousBytesRef = useRef<number>(0);
  const previousTimestampRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const calculateQuality = useCallback(
    (bitrate: number, packetLoss: number, rtt: number): ConnectionQuality => {
      // Disconnected if no data
      if (bitrate === 0 && packetLoss === 0 && rtt === 0) {
        return "disconnected";
      }

      // Calculate quality score based on metrics
      let score = 100;

      // Bitrate scoring (for video, we want at least 500kbps for decent quality)
      if (bitrate < 100000) score -= 40;
      else if (bitrate < 300000) score -= 25;
      else if (bitrate < 500000) score -= 10;

      // Packet loss scoring
      if (packetLoss > 10) score -= 40;
      else if (packetLoss > 5) score -= 25;
      else if (packetLoss > 2) score -= 10;

      // RTT scoring (in ms)
      if (rtt > 500) score -= 30;
      else if (rtt > 300) score -= 20;
      else if (rtt > 150) score -= 10;

      // Map score to quality
      if (score >= 80) return "excellent";
      if (score >= 60) return "good";
      if (score >= 40) return "fair";
      return "poor";
    },
    []
  );

  const collectStats = useCallback(async () => {
    if (!peerConnection || peerConnection.connectionState !== "connected") {
      setStats({
        quality: "disconnected",
        bitrate: 0,
        packetLoss: 0,
        roundTripTime: 0,
      });
      return;
    }

    try {
      const statsReport = await peerConnection.getStats();
      let totalBytesReceived = 0;
      let packetsLost = 0;
      let packetsReceived = 0;
      let roundTripTime = 0;
      let hasRtt = false;

      statsReport.forEach((report) => {
        // Get inbound RTP stats for video
        if (report.type === "inbound-rtp" && report.kind === "video") {
          totalBytesReceived += report.bytesReceived || 0;
          packetsLost += report.packetsLost || 0;
          packetsReceived += report.packetsReceived || 0;
        }

        // Get candidate pair stats for RTT
        if (report.type === "candidate-pair" && report.state === "succeeded") {
          if (report.currentRoundTripTime) {
            roundTripTime = report.currentRoundTripTime * 1000; // Convert to ms
            hasRtt = true;
          }
        }
      });

      // Calculate bitrate
      const now = Date.now();
      const timeDiff = now - previousTimestampRef.current;
      const bytesDiff = totalBytesReceived - previousBytesRef.current;

      let bitrate = 0;
      if (timeDiff > 0 && previousTimestampRef.current > 0) {
        bitrate = (bytesDiff * 8) / (timeDiff / 1000); // bits per second
      }

      previousBytesRef.current = totalBytesReceived;
      previousTimestampRef.current = now;

      // Calculate packet loss percentage
      const totalPackets = packetsReceived + packetsLost;
      const packetLossPercent = totalPackets > 0 ? (packetsLost / totalPackets) * 100 : 0;

      const quality = calculateQuality(bitrate, packetLossPercent, roundTripTime);

      setStats({
        quality,
        bitrate: Math.round(bitrate),
        packetLoss: Math.round(packetLossPercent * 10) / 10,
        roundTripTime: Math.round(roundTripTime),
      });

      console.log("Connection stats:", { quality, bitrate, packetLossPercent, roundTripTime });
    } catch (error) {
      console.error("Error collecting stats:", error);
    }
  }, [peerConnection, calculateQuality]);

  useEffect(() => {
    if (!peerConnection) {
      setStats({
        quality: "disconnected",
        bitrate: 0,
        packetLoss: 0,
        roundTripTime: 0,
      });
      return;
    }

    // Reset previous values
    previousBytesRef.current = 0;
    previousTimestampRef.current = 0;

    // Collect stats every 2 seconds
    intervalRef.current = setInterval(collectStats, 2000);

    // Initial collection
    collectStats();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [peerConnection, collectStats]);

  return stats;
};
